import type { NotificationProps, NotificationType } from '@components/notification';
import { delay } from '@helper-functions/delay';
import { atom, getDefaultStore } from 'jotai';
import type { ReactElement, ReactNode, SyntheticEvent } from 'react';

type PayloadType = {
  text?: string;
  type?: NotificationType;
  duration?: number;
  onClose?: (event?: SyntheticEvent) => void;
  link?: ReactElement;
  onLinkClick?: (event?: SyntheticEvent) => void;
  action?: ReactElement;
  key?: string;
};

export type NotificationPropsWithKey = NotificationProps & { key: string };

export const notificationsAtom = atom<NotificationPropsWithKey[]>([]);

const store = getDefaultStore();

const generateKey = () => `${Date.now() * 1000}.${Math.random() * 10000}`;

const addOne = (payload: NotificationPropsWithKey) => {
  store.set(notificationsAtom, (prev) => [payload, ...prev]);
};

const removeOne = (key: string) => {
  store.set(notificationsAtom, (prev) => prev.filter((n) => n.key !== key));
};

const updateOne = (key: string, payload: NotificationProps) => {
  store.set(notificationsAtom, (prev) =>
    prev.map((n) => (n.key === key ? { ...payload, key } : n)),
  );
};

export const addNotification = (payload: PayloadType) => {
  const key = payload?.key ?? generateKey();
  addOne({ ...payload, key });
  setTimeout(() => removeOne(key), payload?.duration || 3000);
};

export const removeNotification = (key: string) => {
  removeOne(key);
};

export const removeAllNotifications = () => {
  store.get(notificationsAtom).forEach((n) => removeOne(n.key));
};

const isGatewayTimeoutError = (error: unknown): boolean => {
  const networkError = (error as { networkError?: { statusCode?: number } } | null)?.networkError;
  return networkError?.statusCode === 504;
};

export const createAsyncNotification = (
  message: string | ReactNode,
  callback: (...data: unknown[]) => unknown,
  options?: {
    link?: ReactElement;
    onLinkClick?: (event: SyntheticEvent) => void;
    duration?: number;
    loadingMessage?: string;
    timeoutMessage?: string | ReactNode;
    onTimeout?: () => void;
  },
  onCallbackError?: () => unknown,
) => {
  const key = generateKey();
  return async (...data: unknown[]) => {
    addOne({ key, type: 'loading', text: options?.loadingMessage || 'Processing...' });
    try {
      await callback(...data);
      updateOne(key, {
        type: 'success',
        text: message,
        link: options?.link,
        onLinkClick: options?.onLinkClick,
      });
      await delay(options?.duration || 3000);
      removeOne(key);
    } catch (error) {
      if (options?.timeoutMessage && isGatewayTimeoutError(error)) {
        updateOne(key, { type: 'info', text: options.timeoutMessage });
        options.onTimeout?.();
        await delay(8000);
        removeOne(key);
        return;
      }
      if (onCallbackError) {
        onCallbackError();
      }
      if (error instanceof Error) {
        updateOne(key, {
          type: 'error',
          text: error.message || 'Something went wrong please try again...',
        });
      }
      await delay(5000);
      removeOne(key);
    }
  };
};
